import { AnimatePresence, motion } from "framer-motion";
import { groupBy } from "lodash";
import { useMemo } from "react";

import { NotificationType } from "@/apollo/types";
import {
  Banner,
  BannerCloseButton,
  BannerDescription,
  BannerIcon,
  BannerTitle,
} from "@/components/elements/Banner";
import { useInitialSyncsProgressBannerResolver } from "@/features/elt/initial-syncs-progress-banner";
import { useMountEffect } from "@/hooks/useMountEffect";
import useNow from "@/hooks/useNow";
import { useMixpanel } from "@/monitoring/mixpanel";

import { useAccountLevelNotificationsResolver } from "./resolvers/useAccountLevelNotificationsResolver";
import { useContractRenewalNotificationResolver } from "./resolvers/useContractRenewalNotificationResolver";
import { useFreemiumResolver } from "./resolvers/useFreemiumResolver";
import { useSyncsBlockedResolver } from "./resolvers/useSyncsBlockedResolver";
import { useDismissedBannersState } from "./useDismissedBannersState";
import { WorkspaceAlert, useWorkspaceAlerts } from "./useWorkspaceAlerts";

const orderedStatuses: Record<WorkspaceAlert["status"], number> = {
  info: 0,
  warning: 1,
  error: 2,
};

const dedupeTypeGroups = [
  [
    NotificationType.OverdueInvoiceReminder,
    NotificationType.OverdueInvoiceServiceSuspension,
  ],
];

const generateDedupeKey = (alert: WorkspaceAlert) => {
  if (alert.type) {
    const typeGroup = dedupeTypeGroups.find((group) => {
      if (!alert.type) return false;
      return group.includes(alert.type);
    });
    if (typeGroup) {
      return typeGroup.join(",");
    }
    return alert.type;
  }
  return alert.id;
};

function useDedupeAlerts(alerts: WorkspaceAlert[]) {
  const groups = groupBy(alerts, generateDedupeKey);
  return Object.values(groups).flatMap((group) => {
    if (group.length === 1) {
      return group;
    }
    // select the most recent alert
    const mostRecent = group.reduce((a, b) => {
      if (!a.updatedAt && !b.updatedAt) {
        // select based on status
        return orderedStatuses[a.status] >= orderedStatuses[b.status] ? a : b;
      }
      if (!a.updatedAt) {
        return b;
      }
      if (!b.updatedAt) {
        return a;
      }
      return a.updatedAt > b.updatedAt ? a : b;
    }, group[0]);
    return mostRecent;
  });
}

function useAlerts() {
  const alerts = useWorkspaceAlerts(
    useAccountLevelNotificationsResolver,
    useSyncsBlockedResolver,
    useFreemiumResolver,
    useContractRenewalNotificationResolver,
    useInitialSyncsProgressBannerResolver,
  );

  const dedupedAlerts = useDedupeAlerts(alerts);
  const sortedAlerts = dedupedAlerts
    .slice()
    .sort((a, b) => orderedStatuses[b.status] - orderedStatuses[a.status]);

  return sortedAlerts;
}

function useFilteredAlerts(alerts: WorkspaceAlert[], dismissedIds: string[]) {
  return useMemo<Set<string>>(() => {
    if (alerts.length === 0) {
      return new Set();
    }
    const alwaysShowAlertsIds = alerts
      .filter((x) => x.alwaysShow === true)
      .map((x) => x.id);

    const otherAlertIds =
      alerts[0].status === "error"
        ? alerts.filter((x) => x.status === "error").map((x) => x.id)
        : [alerts[0].id];

    const filteredAlwaysShowAlerts = [
      ...alwaysShowAlertsIds,
      ...otherAlertIds,
    ].filter((id) => !dismissedIds.includes(id));

    return new Set(filteredAlwaysShowAlerts);
  }, [alerts, dismissedIds]);
}

export function WorkspaceBanners() {
  const alerts = useAlerts();
  const mixpanel = useMixpanel();

  const [dismissedEntries, dismissBanner] = useDismissedBannersState();

  const now = useNow(60_000).valueOf();

  const effectiveDismissedIds = useMemo(() => {
    const ids: string[] = [];
    for (const entry of dismissedEntries) {
      const alert = alerts.find((a) => a.id === entry.id);
      const duration = alert?.dismissDuration;
      if (duration == null) {
        // Permanent dismissal (or alert no longer active)
        ids.push(entry.id);
      } else if (now - entry.dismissedAt < duration) {
        // Still within dismissal window
        ids.push(entry.id);
      }
    }
    return ids;
  }, [alerts, dismissedEntries, now]);

  const filteredAlerts = useFilteredAlerts(alerts, effectiveDismissedIds);

  const handleDismiss = (alert: WorkspaceAlert) => {
    dismissBanner(alert.id);
    mixpanel.track("Workspace Banner Dismissed", {
      alertId: alert.id,
      type: alert.type,
    });
  };

  return (
    <div className="shrink-0">
      <AnimatePresence initial={false}>
        {alerts.map((alert) => (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{
              opacity: 0,
              scale: 0.9,
              transition: { duration: 0.15 },
            }}
          >
            <BannerContainer
              alert={alert}
              show={filteredAlerts.has(alert.id)}
              dismiss={() => handleDismiss(alert)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function BannerContainer(props: {
  alert: WorkspaceAlert;
  show: boolean;
  dismiss: () => void;
}) {
  const mixpanel = useMixpanel();
  const { alert, dismiss, show } = props;

  useMountEffect(() => {
    if (!show) {
      return;
    }
    const properties = {
      alertId: alert.id,
      type: alert.type || alert.id,
      status: alert.status,
      title: alert.title,
      message:
        typeof alert.message === "string" ? alert.message : "complex content",
      isDismissable: alert.isDismissible,
      alwaysShow: alert.alwaysShow,
      updatedAt: alert.updatedAt,
    };
    mixpanel.track("Workspace Banner Shown", properties);
  });

  if (!show) {
    return null;
  }

  if (typeof alert.render === "function") {
    return alert.render({ dismiss });
  }

  return (
    <Banner status={alert.status} size="sm" action={alert.action}>
      <BannerIcon />
      <BannerTitle>{alert.title}</BannerTitle>
      <BannerDescription>{alert.message}</BannerDescription>
      {alert.isDismissible && (
        <BannerCloseButton
          onClick={() => {
            dismiss();
          }}
        />
      )}
    </Banner>
  );
}
